import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { ExternalToast, toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { extractHashtags } from '@/utils/topicExtraction';
import { useExtractCommunities } from '@/hooks/use-loop-communities';
import { FocalPoint } from '../components/forms/FocalPointSelector';
import { useInfiniteBulletins } from './use-infinite-bulletins';
import { useUploadProgress } from './use-upload-progress';
import { checkForProhibitedContent, getProhibitedContentMessage, applySoftBlocking } from '@/utils/content-moderation';
import { handleMediaUpload } from '@/utils/mediaHandlers';
import { detectSilentUploadFailure } from '@/utils/upload-error-tracker';
import { useCreateMentions } from '@/hooks/use-mentions';
import { bulletinCreateSchema, safeParse } from '@/lib/validation/schemas';

export const useBulletinForm = (isUpdateTab = false, onSuccess?: () => void, isLoopPost = false) => {
  const { user, profile } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isMatureContent, setIsMatureContent] = useState(false);
  const [legacyUploadProgress, setLegacyUploadProgress] = useState<any>({
    progress: 0,
    state: 'idle'
  });
  const [focalPoint, setFocalPoint] = useState<FocalPoint | null>(null);
  const queryClient = useQueryClient();
  const { extractCommunities } = useExtractCommunities();
  const { addOptimisticBulletin, removeOptimisticBulletin, updateOptimisticBulletin } = useInfiniteBulletins(isUpdateTab ? 'updates' : 'pour-toi', 'all');
  const uploadProgressModal = useUploadProgress();
  const { mutate: createMentions } = useCreateMentions();
  // Artstream toggle: default OFF to prevent accidental artstream posts
  const [isArtstream, setIsArtstream] = useState<boolean>(false);
  // Disabled communities for filtering
  const [disabledCommunities, setDisabledCommunities] = useState<Set<string>>(new Set());

  // Helper to emit step updates to the PostFlowOverlay
  const emitStep = (update: any) => {
    try {
      window.dispatchEvent(new CustomEvent('post-flow:update', { detail: update }));
    } catch {}
  };

  const resetForm = useCallback(() => {
    setTitle('');
    setContent('');
    setSelectedImage(null);
    setIsUploading(false);
    setIsMatureContent(false);
    setLegacyUploadProgress({ progress: 0, state: 'idle' });
    setFocalPoint(null);
    setDisabledCommunities(new Set());
    uploadProgressModal.resetProgress();
  }, [uploadProgressModal]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>, isMature: boolean = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    console.log('📸 [BULLETIN FLOW] Media selected:', {
      name: file.name,
      type: file.type,
      size: file.size,
      isMature,
      timestamp: new Date().toISOString()
    });
    
    setSelectedImage(file);
    setIsMatureContent(isMature);
    setLegacyUploadProgress({ progress: 0, state: 'idle' });
    setIsUploading(false);
  };

  const getFileExtension = (file: File): string => {
    const filenameParts = file.name.split('.');
    if (filenameParts.length > 1) {
      const ext = filenameParts.pop()?.toLowerCase();
      if (ext === 'jpeg') return 'jpg';
      if (ext) return ext;
    }
    
    const mimeType = file.type.toLowerCase();
    if (mimeType === 'image/jpeg') return 'jpg';
    if (mimeType === 'image/png') return 'png';
    if (mimeType === 'image/gif') return 'gif';
    if (mimeType === 'video/mp4') return 'mp4';
    
    return '';
  };

  const submitDirectly = async (bulletin: any) => {
    try {
      console.log('💾 [TEXT POST DEBUG] Submitting bulletin to database:', {
        id: bulletin.id,
        content: bulletin.content?.substring(0, 50) + '...',
        has_image: !!bulletin.image_url,
        is_mature: bulletin.is_mature,
        moderation_status: bulletin.moderation_status,
        is_text_only: !bulletin.image_url && !!bulletin.content,
        timestamp: new Date().toISOString()
      });
      
      const { upload_progress, ...bulletinWithoutProblematicFields } = bulletin;

      console.log('🔍 [TEXT POST DEBUG] Clean bulletin object:', {
        keys: Object.keys(bulletinWithoutProblematicFields),
        content_exists: !!bulletinWithoutProblematicFields.content,
        content_length: bulletinWithoutProblematicFields.content?.length || 0,
        user_id_exists: !!bulletinWithoutProblematicFields.user_id,
        fullObject: bulletinWithoutProblematicFields
      });

      const { data, error } = await supabase
        .from('bulletins')
        .insert([bulletinWithoutProblematicFields])
        .select();

      if (error) {
        console.error('❌ [TEXT POST DEBUG] Database insertion failed:', {
          error: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      console.log('✅ [TEXT POST DEBUG] Database insertion successful:', {
        id: data?.[0]?.id,
        content_preview: data?.[0]?.content?.substring(0, 50),
        moderation_status: data?.[0]?.moderation_status,
        created_at: data?.[0]?.created_at,
        is_text_only: !data?.[0]?.image_url && !!data?.[0]?.content,
        full_data: data,
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      console.error('❌ [TEXT POST DEBUG] Failed to submit bulletin directly:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  };

  const storeFocalPoint = (bulletinId: string, focalPoint: FocalPoint) => {
    try {
      const focalPoints = JSON.parse(localStorage.getItem('fomo_focal_points') || '{}');
      focalPoints[bulletinId] = focalPoint;
      localStorage.setItem('fomo_focal_points', JSON.stringify(focalPoints));
    } catch (e) {
      console.error('Failed to store focal point', e);
    }
  };

  const getFocalPoint = (bulletinId: string): FocalPoint | null => {
    try {
      const focalPoints = JSON.parse(localStorage.getItem('fomo_focal_points') || '{}');
      return focalPoints[bulletinId] || null;
    } catch (e) {
      console.error('Failed to get focal point', e);
      return null;
    }
  };

  // directBulletinContent: used for bulletins.content; directLoopsContent: used for loops.content
  const handleSubmit = async (
    e?: React.FormEvent | null,
    focalPoint?: FocalPoint,
    directBulletinContent?: string,
    directImage?: File,
    directLoopsContent?: string,
    overrideDisabledCommunities?: Set<string>,
    directVideo?: File
  ): Promise<boolean> => {
    e?.preventDefault();
    
    console.log('🚀 [TEXT POST DEBUG] Starting bulletin submission:', {
      hasUser: !!user,
      userId: user?.id,
      contentLength: content?.trim()?.length || 0,
      hasImage: !!selectedImage,
      isUpdateTab,
      overrideDisabledCommunitiesSize: overrideDisabledCommunities?.size,
      overrideDisabledCommunitiesArray: overrideDisabledCommunities ? Array.from(overrideDisabledCommunities) : [],
      stateDisabledCommunitiesSize: disabledCommunities.size,
      stateDisabledCommunitiesArray: Array.from(disabledCommunities),
      timestamp: new Date().toISOString()
    });
    
    if (!user) {
      console.error('❌ [TEXT POST DEBUG] No user found');
      toast.error("You must be logged in to post.");
      return false;
    }

    // Validate inputs - use direct parameters if provided, otherwise use state
    const bulletinContent = (directBulletinContent ?? content).trim();
    const loopsContent = (directLoopsContent ?? directBulletinContent ?? content).trim();
    const currentImage = directImage || selectedImage;
    // Unified content used for bulletin storage/moderation. If bulletin-specific content
    // is empty (e.g., text-only modal flow), fall back to loops content.
    const effectiveContent = bulletinContent || loopsContent;

    // Validate input using Zod schema
    const validationResult = safeParse(bulletinCreateSchema, {
      content: effectiveContent || '',
      media_url: undefined, // Will be set after upload
      media_type: currentImage ? (currentImage.type.startsWith('video/') ? 'video' : currentImage.type === 'image/gif' ? 'gif' : 'image') : undefined,
      is_nsfw: isMatureContent,
    });

    if (!validationResult.success) {
      console.error('❌ [VALIDATION] Input validation failed:', (validationResult as any).error);
      toast.error((validationResult as any).error);
      return false;
    }

    // Use the validated and trimmed content from Zod (guaranteed to exist after validation)
    const validatedContent = validationResult.data.content!

    // Allow if at least one of: loopsContent, bulletinContent, or image is provided
    if (!currentImage && !bulletinContent && !loopsContent) {
      console.error('❌ [TEXT POST DEBUG] No content or image provided');
      toast.error("Please add some content or an image to post.");
      return false;
    }
    
    // Debug log the selected image to help diagnose issues
    console.log('📸 [TEXT POST DEBUG] Selected image check:', {
      hasSelectedImage: !!currentImage,
      imageType: currentImage?.type,
      imageSize: currentImage?.size,
      timestamp: new Date().toISOString()
    });

    console.log('✅ [TEXT POST DEBUG] Initial validation passed:', {
      contentPreview: (bulletinContent || loopsContent).substring(0, 50),
      isTextOnly: !currentImage && (!!bulletinContent || !!loopsContent)
    });

    // Start upload progress modal immediately for user feedback
    console.log('[🟢 Upload Flow] Starting upload process');
    uploadProgressModal.startUpload();
    setIsUploading(true);
    // Initialize stepper
    console.log('[🟢 Upload Flow] Step 1: Preparing upload');
    emitStep({ key: 'upload', state: 'in_progress', detail: 'Preparing…' });

    // Check rate limits for bulletin creation
    console.log('[🟢 Upload Flow] Step 2: Checking rate limits');
    uploadProgressModal.updateProgress('validating', 10, 'Checking rate limits...');
    try {
      const session = await supabase.auth.getSession();
      const { data: rateLimitData, error: rateLimitError } = await supabase.functions.invoke('rate-limiter', {
        body: {
          endpoint: 'bulletin-create',
          headers: { 
            authorization: `Bearer ${session.data.session?.access_token}` 
          }
        }
      });

      // Graceful handling when Edge Functions are not available in this environment
      const rlMsg = (rateLimitError?.message || '').toLowerCase();
      if (rlMsg.includes('functions api is not available')) {
        console.warn('[🟢 Upload Flow] Rate limiter unavailable, proceeding without it');
      } else if (rateLimitError || rateLimitData?.error) {
        const errorMessage = rateLimitData?.error || "You're posting too quickly. Please wait a moment before trying again.";
        console.error('[🟢 Upload Flow] Rate limit hit:', errorMessage);
        uploadProgressModal.setError(errorMessage);
        return false;
      }
      console.log('[🟢 Upload Flow] Rate limit check passed');
    } catch (rateLimitError) {
      console.warn('[🟢 Upload Flow] Rate limit check failed, continuing:', rateLimitError);
      // Continue with submission if rate limit check fails
    }

    // DISABLED: Duplicate check (rate limiting already prevents accidental double-posts)
    // This was causing timeouts on slow connections
    console.log('[🟢 Upload Flow] Step 3: Skipping duplicate check (rate limiting active)');
    
    /* DUPLICATE CHECK DISABLED - Can re-enable later if needed
    uploadProgressModal.updateProgress('checking-duplicates', 20, 'Checking for duplicates...');
    if (currentImage) {
      try {
        const formData = new FormData();
        formData.append('file', currentImage);
        
        const session = await supabase.auth.getSession();
        const { data: duplicateData, error: duplicateError } = await supabase.functions.invoke('check-duplicate-upload', {
          body: formData,
          headers: {
            authorization: `Bearer ${session.data.session?.access_token}`
          }
        });

        const dupMsg = (duplicateError?.message || '').toLowerCase();
        if (dupMsg.includes('functions api is not available')) {
          console.warn('[🟢 Upload Flow] Duplicate-check function unavailable, skipping');
        } else if (duplicateError || duplicateData?.isDuplicate) {
          const errorMessage = duplicateData?.message || "This file was already uploaded recently. Please wait before uploading again.";
          console.error('[🟢 Upload Flow] Duplicate detected:', errorMessage);
          uploadProgressModal.setError(errorMessage);
          return false;
        }
        console.log('[🟢 Upload Flow] Duplicate check passed');
      } catch (duplicateError) {
        console.warn('[🟢 Upload Flow] Duplicate check failed, continuing:', duplicateError);
        // Continue with submission if duplicate check fails
      }
    }
    */
    
    // Check content for prohibited words
    console.log('[🟢 Upload Flow] Step 4: Validating content');
    uploadProgressModal.updateProgress('checking-content', 30, 'Validating content...');
    // Bulletin content for moderation and storage - use validated/trimmed content
    let contentToSubmit = validatedContent;
    
    console.log('[🟢 Upload Flow] Checking content:', contentToSubmit?.substring(0, 50));
    if (contentToSubmit) {
      const contentCheck = await checkForProhibitedContent(contentToSubmit);
      if (contentCheck.isProhibited) {
        const errorMessage = getProhibitedContentMessage(
          contentCheck.prohibitedWord,
          contentCheck.category
        );
        console.error('[🟢 Upload Flow] Prohibited content detected:', contentCheck);
        uploadProgressModal.setError(errorMessage);
        return false;
      }
      console.log('[🟢 Upload Flow] Content validation passed');
      contentToSubmit = await applySoftBlocking(contentToSubmit);
    }

    // All validation complete, proceed to upload
    console.log('[🟢 Upload Flow] Step 5: Validation complete, preparing upload');
    uploadProgressModal.updateProgress('uploading', 40, 'Preparing upload...');

    const optimisticId = uuidv4();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tempImageUrl = currentImage ? URL.createObjectURL(currentImage) : null;

    const optimisticBulletin = {
      id: optimisticId,
      title: title?.trim() || null,
      content: contentToSubmit,
      created_at: new Date().toISOString(),
      user_id: user.id,
      is_update: isUpdateTab,
      like_count: 0,
      image_url: tempImageUrl,
      thumbnail_url: null,
      file_type: currentImage ? getFileExtension(currentImage) : null,
      moderation_status: 'pending',
      profiles: {
        username: profile?.username || '',
        profile_image_url: profile?.profile_image_url,
        is_admin: profile?.is_admin || false,
        is_premium: profile?.is_premium || false,
        combo_score: profile?.combo_score || 0
      }
    };

    addOptimisticBulletin(optimisticBulletin);
    setLegacyUploadProgress({ progress: 0, state: 'uploading' });

    try {
      let mediaUrl = null;
      let thumbnailUrl = null;
      let fileType = null;
      let version = null;

      if (currentImage) {
        if (!user.id) {
          throw new Error('User ID is required for upload');
        }

        const isPremiumUser = profile?.is_premium || profile?.is_admin;
        
        try {
          console.log('[🟢 Upload Flow] Step 6: Starting media upload', {
            fileSize: (currentImage.size / 1024 / 1024).toFixed(2) + 'MB',
            fileType: currentImage.type
          });
          uploadProgressModal.updateProgress('uploading', 0, 'Uploading media...');
          
          const result = await handleMediaUpload(
            currentImage,
            user.id,
            isPremiumUser,
            (progress) => {
              setLegacyUploadProgress(progress);
              const percentage = Math.round(progress.progress);
              uploadProgressModal.updateProgress('uploading', percentage, `Uploading... ${percentage}%`);
              if (progress.progress > 0) {
                updateOptimisticBulletin(optimisticId, {
                  upload_progress: progress.progress
                });
              }
              emitStep({ key: 'upload', state: 'in_progress', detail: `${percentage}%` });
            },
            false
          );
          
          mediaUrl = result.url;
          thumbnailUrl = result.thumbnailUrl;
          fileType = getFileExtension(currentImage);
          version = result.version;

          updateOptimisticBulletin(optimisticId, {
            image_url: mediaUrl,
            thumbnail_url: thumbnailUrl,
            file_type: fileType,
            image_version: version,
            upload_progress: 100
          });
          emitStep({ key: 'upload', state: 'success', detail: 'Uploaded' });

        } catch (uploadError) {
          console.error('[🟢 Upload Flow] ❌ Media upload failed:', uploadError);
          setLegacyUploadProgress({ progress: 0, state: 'error' });
          uploadProgressModal.setError('Failed to upload media. Please try again.');
          emitStep({ key: 'upload', state: 'error', detail: 'Upload failed' });
          throw new Error('Failed to upload media');
        } finally {
          if (tempImageUrl) {
            URL.revokeObjectURL(tempImageUrl);
          }
        }
      } else {
        console.log('[POST FLOW] No currentImage provided to handleSubmit. Proceeding as text-only post.');
      }

      // Handle video upload if provided (flip-card feature)
      let videoUrl: string | null = null;
      let videoThumbnailUrl: string | null = null;
      
      if (directVideo && currentImage) { // Only allow video if there's also an image (flip-card)
        console.log('[VIDEO UPLOAD] Uploading flip-card video:', {
          fileName: directVideo.name,
          size: directVideo.size,
          type: directVideo.type
        });
        
        try {
          uploadProgressModal.updateProgress('uploading', 75, 'Uploading video...');
          
          const videoResult = await handleMediaUpload(
            directVideo,
            user.id,
            true, // Allow video uploads (premium feature)
            (progress) => {
              const percentage = Math.round(progress.progress * 0.25 + 75); // 75-100%
              uploadProgressModal.updateProgress('uploading', percentage, `Uploading video... ${percentage}%`);
            },
            false // Skip moderation for videos
          );
          
          videoUrl = videoResult.url;
          videoThumbnailUrl = videoResult.thumbnailUrl || null;
          
          console.log('[VIDEO UPLOAD] Video uploaded successfully:', {
            videoUrl,
            thumbnailUrl: videoThumbnailUrl
          });
          
        } catch (videoError) {
          console.error('[VIDEO UPLOAD] Failed to upload video:', videoError);
          // Don't fail the whole post if video upload fails - just proceed without video
          toast.error('Video upload failed. Proceeding without video.');
          videoUrl = null;
          videoThumbnailUrl = null;
        }
      }

      // Guardrail: if a file was selected but mediaUrl is still missing, do NOT continue.
      if (directImage || selectedImage) {
        const hadFile = !!(directImage || selectedImage);
        const hasMediaUrl = !!mediaUrl;
        console.log('[POST FLOW] File presence vs media URL after upload', { hadFile, hasMediaUrl, mediaUrl });
        if (hadFile && !hasMediaUrl) {
          // Automatically track this silent failure
          detectSilentUploadFailure(true, mediaUrl, effectiveContent);
          
          uploadProgressModal.setError('Image upload did not complete. Please try again.');
          emitStep({ key: 'upload', state: 'error', detail: 'No media URL' });
          // Do not insert bulletin or loop if media was intended but URL is missing
          throw new Error('Aborting post: media file present but media URL missing');
        }
      }

      // All uploaded images will be set to pending moderation for background processing
      let isMature = false; // Default to false, will be determined by background processing
      let moderationResult = null;
      let moderationStatus = 'pending'; // Set to pending for all uploads with images
      let moderatedAt = null;

      if (currentImage && currentImage.type.startsWith('image/')) {
        console.log(' [BULLETIN FLOW] Image upload detected, setting to pending moderation', {
          fileType: currentImage.type,
          fileSize: currentImage.size,
          timestamp: new Date().toISOString()
        });
        
        uploadProgressModal.updateProgress('moderating', 50, 'Starting content moderation...', { canCancel: false });
        moderationStatus = 'pending'; // Will be processed by background function
        window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'moderation', state: 'in_progress', detail: 'Pending review' } }));
      } else {
        // Text-only posts are automatically approved
        console.log(' [TEXT POST DEBUG] Text-only post detected, setting to approved:', {
          hasContent: !!(contentToSubmit || loopsContent),
          contentLength: (contentToSubmit || loopsContent)?.length || 0,
          timestamp: new Date().toISOString()
        });
        moderationStatus = 'approved';
        uploadProgressModal.updateProgress('moderating', 100, 'Content approved');
        window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'moderation', state: 'success', detail: 'Approved' } }));
      }

      // Move to saving step
      console.log('[🟢 Upload Flow] Step 7: Saving post to database');
      uploadProgressModal.updateProgress('saving', 0, 'Saving your post...', { canCancel: false });
      window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'save', state: 'in_progress', detail: 'Saving…' } }));
      // Build bulletin payload and save
      const bulletinToSubmit = {
        id: optimisticId,
        user_id: user.id,
        title: title?.trim() || null,
        content: contentToSubmit,
        image_url: mediaUrl,
        thumbnail_url: thumbnailUrl,
        // video_url removed - bulletins table doesn't have this column (only loops table does)
        file_type: fileType,
        image_version: version,
        is_update: isUpdateTab,
        is_artstream: isArtstream,
        is_mature: isMature,
        moderation_status: moderationStatus,
        moderated_at: moderatedAt,
        created_at: new Date().toISOString(),
        upload_progress: 100
      } as any;

      await submitDirectly(bulletinToSubmit);
      window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'save', state: 'success', detail: 'Saved' } }));
      
      // Add optimistic loop to cache IMMEDIATELY so it appears in UI
      const optimisticLoop = {
        id: optimisticId, // Will be updated when real loop is created
        user_id: user.id,
        content: contentToSubmit || '',
        image_url: mediaUrl,
        thumbnail_url: thumbnailUrl,
        video_url: videoUrl,
        view_count: 0,
        like_count: 0,
        comment_count: 0,
        reblog_count: 0,
        quote_count: 0,
        remix_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_active: true,
        is_artstream: isArtstream,
        is_mature: isMature,
        user: {
          username: profile?.username || '',
          display_name: profile?.display_name || '',
          profile_image_url: profile?.profile_image_url,
          tag_code: (profile as any)?.tag_code,
          tag_emote: (profile as any)?.tag_emote,
          community_tag_id: (profile as any)?.community_tag_id,
          community_tag: (profile as any)?.community_tag,
          is_admin: profile?.is_admin,
          is_partner: profile?.is_partner
        },
        is_liked: false,
        is_viewed: false
      };
      
      // Add optimistic loop to all loops query caches
      queryClient.setQueriesData(
        { queryKey: ['loops'] },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page: any[], index: number) => 
              index === 0 ? [optimisticLoop, ...page] : page
            )
          };
        }
      );
      
      // Invalidate queries to refresh other feeds
      queryClient.invalidateQueries({ queryKey: [isUpdateTab ? 'updates' : 'pour-toi'] });
      queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'] });
      
      console.log('[🟢 Upload Flow] Optimistic loop added - post should appear immediately in UI');
      
      // Create mentions for bulletin
      if (contentToSubmit && user?.id) {
        createMentions({
          contentType: 'bulletin',
          contentId: optimisticId,
          text: contentToSubmit,
          mentionerUserId: user.id
        });
      }

      // Cross-post to Loops in background with retries (UI already updated above)
      const createLoopInBackground = async () => {
        // Wait for media URL to become reachable with a few attempts
        const waitForMediaUrl = async (url: string | null, attempts = 3, baseDelayMs = 800): Promise<boolean> => {
          if (!url) return true;
          for (let i = 1; i <= attempts; i++) {
            try {
              const res = await fetch(url, { method: 'HEAD' });
              if (res.ok) {
                console.log(`[CROSS-POST] Media URL reachable on attempt ${i}`);
                return true;
              }
            } catch (err) {
              console.warn(`[CROSS-POST] waitForMediaUrl HEAD failed (attempt ${i}/${attempts})`, err);
            }
            const delay = baseDelayMs * Math.pow(2, i - 1);
            await new Promise((r) => setTimeout(r, delay));
          }
          return false;
        };
        const validateMediaUrl = async (url: string | null): Promise<boolean> => {
          if (!url) return true;
          try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
          } catch (error) {
            console.warn('[CROSS-POST] Media URL validation failed:', { url, error });
            return false;
          }
        };

        const maxAttempts = 3;
        const baseDelay = 1000;

        const attemptCrossPost = async (attempt = 1): Promise<boolean> => {
          try {
            // Validate media on first attempt
            if (bulletinToSubmit.image_url) {
              const isValidImage = await validateMediaUrl(bulletinToSubmit.image_url);
              if (!isValidImage && attempt === 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }

            window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'crosspost', state: attempt === 1 ? 'in_progress' : 'pending', detail: attempt === 1 ? 'Posting to Loops…' : `Retry ${attempt}` } }));

            // If an image is expected, wait for it to be actually reachable before inserting.
            if (bulletinToSubmit.image_url) {
              const ready = await waitForMediaUrl(bulletinToSubmit.image_url, 3, 700);
              if (!ready) {
                console.warn('[CROSS-POST] Media not reachable yet, will retry without inserting a null image_url', {
                  attempt,
                  url: bulletinToSubmit.image_url
                });
                if (attempt < maxAttempts) {
                  const delay = baseDelay * Math.pow(2, attempt - 1);
                  await new Promise((resolve) => setTimeout(resolve, delay));
                  return attemptCrossPost(attempt + 1);
                } else {
                  window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'crosspost', state: 'error', detail: 'Media not ready' } }));
                  toast.error('Cross-posting to Loops failed', {
                    description: 'Image was not ready in time. Your bulletin is posted; please retry Loops later.',
                  });
                  return false;
                }
              }
            }

            const { data: loopData, error: loopError } = await supabase
              .from('loops')
              .insert({
                user_id: user.id,
                content: contentToSubmit || '', // SECURITY FIX: Use censored content, not raw loopsContent
                is_active: true,
                is_artstream: bulletinToSubmit.is_artstream || false, // Inherit artstream flag from bulletin
                image_url: bulletinToSubmit.image_url || null,
                thumbnail_url: bulletinToSubmit.thumbnail_url || null,
                video_url: videoUrl || null, // Only include if video was actually uploaded (premium feature)
                bulletin_id: bulletinToSubmit.id, // Link to bulletin for shared engagement
                moderation_status: bulletinToSubmit.moderation_status || 'approved', // Inherit moderation status from bulletin
                is_mature: bulletinToSubmit.is_mature || false // Inherit mature flag from bulletin
              })
              .select();

            if (loopError) {
              console.error('[CROSS-POST] Failed to create loop (attempt ' + attempt + '):', {
                error: loopError.message,
                code: loopError.code,
                bulletin_id: bulletinToSubmit.id,
                attempt,
                timestamp: new Date().toISOString()
              });
              window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'crosspost', state: 'error', detail: loopError.message || 'Failed' } }));

              if (attempt < maxAttempts && (
                loopError.code === 'PGRST116' ||
                loopError.message.includes('timeout') ||
                loopError.message.includes('network') ||
                loopError.message.includes('connection')
              )) {
                const delay = baseDelay * Math.pow(2, attempt - 1);
                await new Promise(resolve => setTimeout(resolve, delay));
                return attemptCrossPost(attempt + 1);
              }

              if (attempt === maxAttempts) {
                toast.error('Cross-posting to Loops failed', {
                  description: 'Your post was saved but may not appear in Loops feed. Please try refreshing.',
                });
              }
              return false;
            } else {
              console.log('[CROSS-POST] Loop created successfully:', {
                loop_id: loopData?.[0]?.id,
                bulletin_id: bulletinToSubmit.id,
                content_preview: (loopsContent || '').slice(0, 50),
                attempt,
                timestamp: new Date().toISOString()
              });
              
              // Update bulletin with loop_id to complete the bidirectional link
              if (loopData?.[0]?.id) {
                await supabase
                  .from('bulletins')
                  .update({ loop_id: loopData[0].id })
                  .eq('id', bulletinToSubmit.id);
                console.log('[CROSS-POST] Bulletin linked to loop:', {
                  bulletin_id: bulletinToSubmit.id,
                  loop_id: loopData[0].id
                });
                
                // Create mentions for loop (use censored content)
                if (contentToSubmit && user?.id) {
                  createMentions({
                    contentType: 'loop',
                    contentId: loopData[0].id,
                    text: contentToSubmit,
                    mentionerUserId: user.id
                  });
                }
              }
              
              window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'crosspost', state: 'success', detail: 'Posted to Loops' } }));

              if (loopData?.[0]?.id && extractCommunities) {
                window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'communities', state: 'in_progress', detail: 'Analyzing…' } }));
                
                // DEBUG: Log extraction parameters
                console.log('[COMMUNITIES DEBUG] Starting extraction:', {
                  loopId: loopData[0].id,
                  content: (contentToSubmit || '').substring(0, 100),
                  imageUrl: bulletinToSubmit.image_url,
                  disabledCount: (overrideDisabledCommunities || disabledCommunities).size,
                  disabledIds: Array.from(overrideDisabledCommunities || disabledCommunities),
                  timestamp: new Date().toISOString()
                });
                
                extractCommunities(
                  loopData[0].id,
                  contentToSubmit || '',
                  bulletinToSubmit.image_url,
                  overrideDisabledCommunities || disabledCommunities
                )
                  .then((result) => {
                    console.log('[COMMUNITIES DEBUG] Extraction SUCCESS:', {
                      loopId: loopData[0].id,
                      communitiesExtracted: result?.communities?.length || 0,
                      communityNames: result?.communities?.map(c => c.communityName) || [],
                      topicsCount: result?.topics?.length || 0
                    });
                    console.log('[COMMUNITIES] Successfully extracted communities for loop:', loopData[0].id);
                    window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'communities', state: 'success', detail: 'Tagged' } }));
                  })
                  .catch((error) => {
                    console.error('[COMMUNITIES DEBUG] Extraction FAILED:', {
                      error: error?.message || 'Unknown error',
                      stack: error?.stack,
                      loopId: loopData[0].id,
                      content: (contentToSubmit || '').substring(0, 100),
                      timestamp: new Date().toISOString()
                    });
                    console.error('[COMMUNITIES] Failed to extract communities:', error);
                    window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'communities', state: 'error', detail: 'Failed' } }));
                    
                    // Show error to user
                    toast.error('Community tagging failed', {
                      description: `Could not tag communities: ${error?.message || 'Unknown error'}`
                    });
                  });
              }

              // Refresh loops to replace optimistic data with real DB data
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['loops'] }),
                queryClient.invalidateQueries({ queryKey: ['bulletins'] }),
                queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'] })
              ]);
              
              console.log('[CROSS-POST] Loop created in DB, optimistic data replaced with real data');

              if (attempt > 1 && bulletinToSubmit.image_url) {
                toast.success('Post successfully cross-posted to Loops!');
              }
              return true;
            }
          } catch (error) {
            console.error('[CROSS-POST] Unexpected error creating loop (attempt ' + attempt + '):', {
              error: error instanceof Error ? error.message : 'Unknown error',
              bulletin_id: bulletinToSubmit.id,
              attempt,
              timestamp: new Date().toISOString()
            });

            if (attempt < maxAttempts) {
              const delay = baseDelay * Math.pow(2, attempt - 1);
              await new Promise(resolve => setTimeout(resolve, delay));
              return attemptCrossPost(attempt + 1);
            }

            toast.error('Cross-posting to Loops failed', {
              description: 'Your post was saved but may not appear in Loops feed. Please try refreshing.',
            });
            window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'crosspost', state: 'error', detail: 'Failed' } }));
            return false;
          }
        };

        return attemptCrossPost();
      };
      
      // Execute background loop creation with proper error handling
      createLoopInBackground().catch(error => {
        console.error('[CROSS-POST] Critical error in background cross-posting:', error);
        // Don't show error to user here as it's already handled in the function
      });
      
      // Handle post-upload feedback based on content type
      if (selectedImage && selectedImage.type.startsWith('image/')) {
        uploadProgressModal.updateProgress('moderating', 75, 'Content moderation in progress... This may take up to 60 seconds.', { canCancel: false });
        
        // Auto-close modal after 8 seconds for moderated content
        setTimeout(() => {
          uploadProgressModal.updateProgress('moderating', 100, 'Upload complete!');
          uploadProgressModal.setComplete();
          setTimeout(() => {
            uploadProgressModal.closeModal();
            // Show moderation in progress toast AFTER modal closes
            // Only show this toast for image posts, not for loops
            if (!isLoopPost) {
              toast.info("Content moderation in progress...", {
                description: "Your post will appear once moderation is complete.",
              });
            }
          }, 1000);
        }, 8000);
      } else {
        // Text-only posts show immediate success
        uploadProgressModal.setComplete();
        
        // Auto-close modal after 2 seconds for text-only posts
        setTimeout(() => {
          uploadProgressModal.closeModal();
          // Show immediate success for text posts, but only if not a loop post
          // (loops have their own visual feedback)
          if (!isLoopPost) {
            toast.success(isUpdateTab ? 'Update posted!' : 'Post successful!');
          }
        }, 2000);
      }
      // Constellations step (placeholder): emit success for now
      emitStep({ key: 'constellations', state: 'in_progress', detail: 'Finding related content…' });
      setTimeout(() => emitStep({ key: 'constellations', state: 'success', detail: 'Ready' }), 500);
      
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
      
      return true;
    } catch (error) {
      console.error('Error posting bulletin:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to post. Please try again.";
      uploadProgressModal.setError(errorMessage);
      // Remove redundant error toast - modal already shows the error
      
      removeOptimisticBulletin(optimisticId);
      queryClient.invalidateQueries({ queryKey: [isUpdateTab ? 'updates' : 'pour-toi'] });
      
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = useCallback(() => {
    if (isUploading) {
      // TODO: Implement actual upload cancellation logic here
      console.log('Upload cancellation requested');
    }
    uploadProgressModal.closeModal();
    resetForm();
  }, [isUploading, uploadProgressModal, resetForm]);

  const handleRetry = useCallback(() => {
    uploadProgressModal.resetProgress();
    // Don't auto-restart, let user manually retry
  }, [uploadProgressModal]);

  return {
    title,
    setTitle,
    content,
    setContent,
    selectedImage,
    setSelectedImage,
    isUploading,
    uploadProgress: legacyUploadProgress,
    uploadModal: {
      isOpen: uploadProgressModal.isModalOpen,
      state: uploadProgressModal.progressState,
      onCancel: handleCancel,
      onRetry: handleRetry
    },
    handleImageSelect,
    handleSubmit,
    resetForm,
    isArtstream,
    setIsArtstream,
    disabledCommunities,
    setDisabledCommunities,
  };
};
